import { useCallback, useEffect, useMemo, useState } from "react";
import {
	CalendarCheck,
	CheckCircle2,
	Clock,
	Download,
	RefreshCw,
	Server,
	Ticket,
	Users,
	XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadDashboardCsv, getDashboard } from "./dashboardApi";

const nf = new Intl.NumberFormat("es-AR");

/** HH:MM from an ISO start datetime, in the efector's local tz (matches the board). */
function formatTime(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleTimeString("es-AR", {
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return "—";
	}
}

/** Turn status → Spanish label, in a sensible lifecycle order for the breakdown. */
const STATUS_LABELS = {
	booked: "Reservado",
	confirmed: "Confirmado",
	arrived: "Presente",
	called: "Llamado",
	fulfilled: "Atendido",
	cancelled: "Cancelado",
	noshow: "Ausente",
};
const STATUS_ORDER = [
	"booked",
	"confirmed",
	"arrived",
	"called",
	"fulfilled",
	"cancelled",
	"noshow",
];

function pct(part, total) {
	if (!total) return null;
	return Math.round((part / total) * 100);
}

/** A single KPI tile: label, big value, an optional sub-line and icon. */
function StatTile({ icon: Icon, label, value, sub, tone }) {
	return (
		<div className="bg-card flex flex-col gap-1 rounded-xl border p-4">
			<div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
				{Icon ? <Icon className="size-4" /> : null}
				<span>{label}</span>
			</div>
			<div className={`text-3xl font-bold tracking-tight ${tone ?? ""}`}>
				{value}
			</div>
			{sub ? (
				<div className="text-muted-foreground text-xs">{sub}</div>
			) : null}
		</div>
	);
}

/** Horizontal magnitude bars for today's turns by status (single hue = comparison). */
function StatusBars({ byStatus }) {
	const rows = STATUS_ORDER.map((k) => ({
		key: k,
		label: STATUS_LABELS[k],
		n: byStatus?.[k] ?? 0,
	}));
	const max = Math.max(1, ...rows.map((r) => r.n));
	const anything = rows.some((r) => r.n > 0);
	return (
		<div className="bg-card rounded-xl border p-4">
			<h2 className="text-sm font-semibold">Turnos por estado</h2>
			{!anything ? (
				<p className="text-muted-foreground mt-4 text-sm">
					Sin turnos registrados para este día.
				</p>
			) : (
				<ul className="mt-3 space-y-2">
					{rows.map((r) => (
						<li key={r.key} className="flex items-center gap-3">
							<span className="text-muted-foreground w-20 shrink-0 text-xs">
								{r.label}
							</span>
							<div className="bg-muted h-4 flex-1 overflow-hidden rounded">
								<div
									className="bg-primary h-full rounded"
									style={{ width: `${(r.n / max) * 100}%` }}
								/>
							</div>
							<span className="w-8 shrink-0 text-right text-sm font-medium tabular-nums">
								{nf.format(r.n)}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

/**
 * Horizontal ranked bars for a "most used" breakdown (already sorted desc by the
 * backend). `rows` = [{ key?, label, count }]; single hue = pure magnitude.
 */
function RankBars({ title, rows, empty }) {
	const max = Math.max(1, ...rows.map((r) => r.count));
	return (
		<div className="bg-card rounded-xl border p-4">
			<h2 className="text-sm font-semibold">{title}</h2>
			{rows.length === 0 ? (
				<p className="text-muted-foreground mt-4 text-sm">{empty}</p>
			) : (
				<ul className="mt-3 space-y-2">
					{rows.map((r) => (
						<li
							key={r.key ?? r.label}
							className="flex items-center gap-3"
						>
							<span
								className="text-muted-foreground w-28 shrink-0 truncate text-xs"
								title={r.label}
							>
								{r.label}
							</span>
							<div className="bg-muted h-4 flex-1 overflow-hidden rounded">
								<div
									className="bg-primary h-full rounded"
									style={{
										width: `${(r.count / max) * 100}%`,
									}}
								/>
							</div>
							<span className="w-8 shrink-0 text-right text-sm font-medium tabular-nums">
								{nf.format(r.count)}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

/** Compact 7-day column chart: total per day, with the attended portion filled. */
function TrendChart({ trend }) {
	const max = Math.max(1, ...(trend ?? []).map((d) => d.total));
	return (
		<div className="bg-card rounded-xl border p-4">
			<div className="flex items-center justify-between">
				<h2 className="text-sm font-semibold">Turnos últimos 7 días</h2>
				<div className="text-muted-foreground flex items-center gap-3 text-xs">
					<span className="flex items-center gap-1">
						<span className="bg-primary inline-block size-2 rounded-sm" />
						Atendidos
					</span>
					<span className="flex items-center gap-1">
						<span className="bg-muted inline-block size-2 rounded-sm" />
						Total
					</span>
				</div>
			</div>
			<div className="mt-4 flex h-32 items-end gap-2">
				{(trend ?? []).map((d) => {
					const day = new Date(`${d.date}T00:00:00`);
					const weekday = day.toLocaleDateString("es-AR", {
						weekday: "short",
					});
					const totalPct = (d.total / max) * 100;
					const presentPct = d.total
						? (d.present / d.total) * 100
						: 0;
					return (
						<div
							key={d.date}
							className="flex flex-1 flex-col items-center gap-1"
							title={`${weekday} ${d.date}\nTotal: ${d.total} · Atendidos: ${d.present} · Confirmados: ${d.confirmed} · Cancelados: ${d.cancelled}`}
						>
							<div className="flex w-full flex-1 items-end">
								<div
									className="bg-muted relative w-full overflow-hidden rounded-t"
									style={{
										height: `${Math.max(totalPct, d.total ? 4 : 0)}%`,
									}}
								>
									<div
										className="bg-primary absolute inset-x-0 bottom-0 rounded-t"
										style={{ height: `${presentPct}%` }}
									/>
								</div>
							</div>
							<span className="text-muted-foreground text-[10px] capitalize">
								{weekday}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

/** A status row (icon + label + state chip) for the system-health panel. */
function HealthRow({ icon: Icon, label, ok, warn, okText, badText }) {
	const tone = ok
		? "text-emerald-600 dark:text-emerald-400"
		: warn
			? "text-amber-600 dark:text-amber-400"
			: "text-rose-600 dark:text-rose-400";
	const StateIcon = ok ? CheckCircle2 : warn ? Clock : XCircle;
	return (
		<li className="flex items-center justify-between gap-2 text-sm">
			<span className="text-muted-foreground flex items-center gap-2">
				<Icon className="size-4" />
				{label}
			</span>
			<span className={`flex items-center gap-1 font-medium ${tone}`}>
				<StateIcon className="size-4" />
				{ok ? okText : badText}
			</span>
		</li>
	);
}

function SystemStatus({ andes, miPaseConfigured }) {
	const andesOk = andes?.available;
	return (
		<div className="bg-card rounded-xl border p-4">
			<h2 className="text-sm font-semibold">Estado del sistema</h2>
			<ul className="mt-3 space-y-3">
				<HealthRow
					icon={Server}
					label="ANDES"
					ok={andesOk}
					okText={andes?.writesAvailable ? "En línea" : "Solo lectura"}
					warn={andesOk && !andes?.writesAvailable}
					badText="No disponible"
				/>
				<HealthRow
					icon={Ticket}
					label="Mi Pase"
					ok={miPaseConfigured}
					okText="Configurado"
					warn={!miPaseConfigured}
					badText="Sin configurar"
				/>
			</ul>
		</div>
	);
}

function TilesSkeleton() {
	return (
		<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
			{Array.from({ length: 8 }).map((_, i) => (
				<Skeleton key={i} className="h-24 rounded-xl" />
			))}
		</div>
	);
}

export default function Dashboard() {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [exporting, setExporting] = useState(false);
	// Empty string = today (server decides the efector-local day). A picked day
	// pins the dashboard to that date.
	const [date, setDate] = useState("");
	// The server's notion of "today", captured whenever we load without a pinned
	// date. Used as the picker's max so selecting a past day can't cap it below
	// today (otherwise you couldn't navigate back to today via the picker).
	const [serverToday, setServerToday] = useState("");

	const load = useCallback(
		async (silent = false) => {
			if (silent) setRefreshing(true);
			else setLoading(true);
			try {
				const res = await getDashboard(date || undefined);
				setData(res);
				if (!date && res?.date) setServerToday(res.date);
			} catch {
				// toasted by the global interceptor
			} finally {
				setLoading(false);
				setRefreshing(false);
			}
		},
		[date],
	);

	useEffect(() => {
		load();
		// Refresh quietly every 60s so the home screen stays current for reception.
		const id = setInterval(() => load(true), 60000);
		return () => clearInterval(id);
	}, [load]);

	const isToday = !date || date === data?.date;

	const onExport = useCallback(async () => {
		setExporting(true);
		try {
			await downloadDashboardCsv(date || data?.date || undefined);
		} catch {
			// toasted by the global interceptor
		} finally {
			setExporting(false);
		}
	}, [date, data]);

	const today = data?.today;
	const presentRate = useMemo(
		() => pct(today?.present ?? 0, today?.total ?? 0),
		[today],
	);

	return (
		<div className="mx-auto max-w-6xl">
			<div className="flex items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						Panel
					</h1>
					<p className="text-muted-foreground mt-2 text-sm">
						Resumen operativo del día
						{data?.date ? ` · ${data.date}` : ""}
						{!isToday ? " (día seleccionado)" : ""}.
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<input
						type="date"
						value={date || data?.date || ""}
						max={serverToday || data?.date}
						onChange={(e) => setDate(e.target.value)}
						aria-label="Seleccionar día"
						className="border-input bg-background h-9 rounded-md border px-3 text-sm shadow-sm"
					/>
					{!isToday ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setDate("")}
						>
							Hoy
						</Button>
					) : null}
					<Button
						variant="outline"
						size="sm"
						onClick={onExport}
						disabled={loading || exporting || !data}
					>
						<Download className="size-4" />
						Exportar CSV
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => load(true)}
						disabled={loading || refreshing}
					>
						<RefreshCw
							className={`size-4 ${refreshing ? "animate-spin" : ""}`}
						/>
						Actualizar
					</Button>
				</div>
			</div>

			{loading ? (
				<div className="mt-6 space-y-4">
					<TilesSkeleton />
					<Skeleton className="h-64 rounded-xl" />
				</div>
			) : !data ? (
				<p className="text-muted-foreground mt-6 text-sm">
					No se pudieron cargar las estadísticas.
				</p>
			) : (
				<div className="mt-6 space-y-4">
					{/* Row 1 — today's turns + pilot metrics, all in one row */}
					<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
						<StatTile
							icon={CalendarCheck}
							label="Turnos hoy"
							value={nf.format(today.total)}
						/>
						<StatTile
							icon={Users}
							label="Presentes / Atendidos"
							value={nf.format(today.present)}
							sub={
								presentRate == null
									? "sin turnos"
									: `${presentRate}% del total`
							}
						/>
						<StatTile
							icon={XCircle}
							label="Cancelados / Ausentes"
							value={nf.format(today.cancelledNoshow)}
							tone={
								today.cancelledNoshow > 0
									? "text-rose-600 dark:text-rose-400"
									: undefined
							}
						/>
						<StatTile
							icon={Ticket}
							label="Pases Mi Pase"
							value={nf.format(data.miPase.issuedToday)}
							sub={
								data.miPase.pending > 0
									? `${data.miPase.pending} pendiente${data.miPase.pending === 1 ? "" : "s"}`
									: "emitidos"
							}
						/>
					</div>

					{/* Row 3 — charts + system status */}
					<div className="grid gap-4 lg:grid-cols-2">
						<StatusBars byStatus={today.byStatus} />
						<TrendChart trend={data.trend} />
					</div>

					{/* Row 4 — most-booked prestaciones and horarios (selected day) */}
					<div className="grid gap-4 lg:grid-cols-2">
						<RankBars
							title="Prestaciones más reservadas"
							rows={data.topSpecialties ?? []}
							empty="Sin turnos registrados para este día."
						/>
						<RankBars
							title="Horarios más reservados"
							rows={(data.topStartTimes ?? []).map((r) => ({
								key: r.start,
								label: formatTime(r.start),
								count: r.count,
							}))}
							empty="Sin turnos registrados para este día."
						/>
					</div>

					<SystemStatus
						andes={data.andes}
						miPaseConfigured={data.miPaseConfigured}
					/>
				</div>
			)}
		</div>
	);
}
